/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { riskPolicyChallengesAcknowledge } from "../funcs/riskPolicyChallengesAcknowledge.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { AcknowledgeRiskPolicyChallengeResponseBody } from "../models/components/acknowledgeriskpolicychallengeresponsebody.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  AcknowledgeRiskPolicyChallengeRequest,
  AcknowledgeRiskPolicyChallengeSecurity,
} from "../models/operations/acknowledgeriskpolicychallenge.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RiskAcknowledgePolicyChallengeMutationVariables = {
  request: AcknowledgeRiskPolicyChallengeRequest;
  security?: AcknowledgeRiskPolicyChallengeSecurity | undefined;
  options?: RequestOptions;
};

export type RiskAcknowledgePolicyChallengeMutationData =
  AcknowledgeRiskPolicyChallengeResponseBody;

export type RiskAcknowledgePolicyChallengeMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * acknowledgeRiskPolicyChallenge risk
 *
 * @remarks
 * Acknowledge a risk policy warn/challenge from a warning-link token. Records the acknowledgement so the user's retried action proceeds; self-service (no admin approval).
 */
export function useRiskAcknowledgePolicyChallengeMutation(
  options?: MutationHookOptions<
    RiskAcknowledgePolicyChallengeMutationData,
    RiskAcknowledgePolicyChallengeMutationError,
    RiskAcknowledgePolicyChallengeMutationVariables
  >,
): UseMutationResult<
  RiskAcknowledgePolicyChallengeMutationData,
  RiskAcknowledgePolicyChallengeMutationError,
  RiskAcknowledgePolicyChallengeMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRiskAcknowledgePolicyChallengeMutation(client, options),
    ...options,
  });
}

export function mutationKeyRiskAcknowledgePolicyChallenge(): MutationKey {
  return ["@gram/client", "policyChallenges", "acknowledge"];
}

export function buildRiskAcknowledgePolicyChallengeMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RiskAcknowledgePolicyChallengeMutationVariables,
  ) => Promise<RiskAcknowledgePolicyChallengeMutationData>;
} {
  return {
    mutationKey: mutationKeyRiskAcknowledgePolicyChallenge(),
    mutationFn: function riskAcknowledgePolicyChallengeMutationFn({
      request,
      security,
      options,
    }): Promise<RiskAcknowledgePolicyChallengeMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(riskPolicyChallengesAcknowledge(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
